/**
 * SalesContactDialog — Modal con formulario para contactar a ventas Dealider.
 *
 * Se usa desde:
 *  - PlanRecommenderWizard (cuando el plan recomendado es SAS)
 *  - Plan cards en la landing (cuando el plan tiene is_sas_only=true)
 *
 * POST /api/public/sales-contact → persiste lead en sales_leads para que
 * la ruleta interna de Dealider asigne a un ejecutivo.
 */
import { useState } from 'react';
import { APP_NAME } from '@/lib/brand';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Check, Loader2, Phone } from 'lucide-react';

export default function SalesContactDialog({ open, context = {}, onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleClose = () => {
    onClose();
    // Reset solo después de un momento para evitar el flash al cerrar
    setTimeout(() => { setForm({ name: '', email: '', phone: '', company: '', message: '' }); setDone(false); }, 250);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/api/public/sales-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          plan_id: context.plan_id || '',
          industry: context.industry || '',
        }),
      });
      setDone(true);
    } catch {
      // noop — backend siempre devuelve 200 si los campos son válidos
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md bg-slate-950 border-white/10 text-white" data-testid="sales-contact-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-cyan-400/15 flex items-center justify-center mb-3">
            {done ? <Check className="w-6 h-6 text-emerald-300" /> : <Phone className="w-6 h-6 text-cyan-300" />}
          </div>
          <DialogTitle data-testid="sales-contact-title">{done ? '¡Recibimos tu solicitud!' : 'Hablemos con un experto {APP_NAME}'}</DialogTitle>
          <DialogDescription className="text-white/70">
            {done
              ? 'Un ejecutivo te contactará en menos de 24 hrs. Mientras tanto puedes seguir explorando la plataforma.'
              : 'Déjanos tus datos y un ejecutivo te contactará para entender tus necesidades y proponerte un plan a tu medida.'}
          </DialogDescription>
        </DialogHeader>

        {!done && (
          <div className="space-y-3" data-testid="sales-contact-form">
            <div>
              <Label className="text-xs text-white/70">Nombre completo *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10" data-testid="sales-contact-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-white/70">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10" data-testid="sales-contact-email" />
              </div>
              <div>
                <Label className="text-xs text-white/70">Teléfono *</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+52 55 ..." className="bg-white/5 border-white/10" data-testid="sales-contact-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-white/70">Empresa</Label>
              <Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="bg-white/5 border-white/10" data-testid="sales-contact-company" />
            </div>
            <div>
              <Label className="text-xs text-white/70">¿Qué necesitas? (opcional)</Label>
              <Textarea rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="bg-white/5 border-white/10 text-xs" data-testid="sales-contact-message" />
            </div>
            <Button
              onClick={submit}
              disabled={submitting || !form.name.trim() || !form.email.trim() || !form.phone.trim()}
              className="w-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold mt-2"
              data-testid="sales-contact-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
              Solicitar contacto
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
